import { Attribute } from '@df/catalog/attribute/attribute';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { EdependencyLevel, IMetaData, Model, TLoadDependeciesParams } from '@df/catalog/model';
import { IBundleSelectionOptionsData, IBundleSelectionProduct } from '@df/catalog/product/bundle-option-selection';
import { BundleSelection } from '@df/catalog/product/bundle-selection';
import { Stock, TStockBasketTest } from '@df/catalog/stock/stock';
import { StockResource } from '@df/catalog/stock/stock.resource';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { optionColorPipe } from '@df/ui/common/option-color.pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { PriceService, TaxMode } from '@df/ui/price/price.service';
import { inDateRange } from '@df/util/fn/in-date-range';
import { Nexus } from '@tomandco/nexus-api-types';
import clone from 'lodash-es/clone';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import defaults from 'lodash-es/defaults';
import difference from 'lodash-es/difference';
import each from 'lodash-es/each';
import filter from 'lodash-es/filter';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import flattenDeep from 'lodash-es/flattenDeep';
import intersection from 'lodash-es/intersection';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import keyBy from 'lodash-es/keyBy';
import keys from 'lodash-es/keys';
import map from 'lodash-es/map';
import matches from 'lodash-es/matches';
import omit from 'lodash-es/omit';
import omitBy from 'lodash-es/omitBy';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';
import union from 'lodash-es/union';
import uniq from 'lodash-es/uniq';
import values from 'lodash-es/values';

export declare type IProductData = IProductDataV1 | IProductDataV2 | IProductDataV3;

export interface IProductMediaItem extends Nexus.Lib.Catalog.Type.IProductMediaItem {}

export interface IProductMetaData extends IMetaData {
    availability?: string;
}

export interface IProductDataOptions {
    configurable?: IProductDataOptionsConfigurable;
    bundle?: IProductDataBundleOption[];
    custom: IProductDataCustomOption[];
    grouped: IProductDataGroupedOption[];
}

export interface IProductDataOptionsConfigurable {
    [s: string]: {
        [s: number]: number[];
    };
}

export interface IProductDataCustomOption {
    id: number;
    type: string;
    title: string;
    price?: number;
    price_type?: string;
    max?: number;
    req: boolean;
    values?: IProductDataCustomOptionSelection[];
}

export interface IProductDataCustomOptionSelection {
    value: number;
    title: string;
    price?: number;
    image?: string;
}

export interface IProductDataBundleOption {
    children: IProductDataBundleOptionChild[];
    id: number;
    required: boolean;
    defaultSelection?: number | number[];
    type: string;
    title: string;
}
export interface IProductDataBundleOptionChild {
    qty: number;
    fixedQty?: number;
    id: number;
    position?: number;
    selection_id: number;
    product?: Product; // injected when using getter
}

/**
 * Grouped product option
 */
export interface IProductDataGroupedOption {
    // qty
    qty: number;
    // product id
    id: number;
}

export interface IProductConfigurableSelection {
    [attributeCode: string]: number | undefined;
}

export interface IProductDataV1 extends Nexus.Lib.Catalog.Model.Product.Data.V1 {}

export enum EBundlePriceType {
    dynamic = 0,
    static = 1
}

export interface IProductDataV2 extends IProductDataV1, Nexus.Lib.Catalog.Model.Product.Data.V2 {}

export interface IProductDataV3 extends IProductDataV2, Nexus.Lib.Catalog.Model.Product.Data.V3 {}

export interface IProductDataLink extends Nexus.Lib.Catalog.Type.IProductDataLink {}

export class Product<T extends IProductData = IProductData> extends Model<T, number> {
    /**
     * Object type id
     * @type {String}
     */
    static override type = 'product';

    /**
     * If model is routable
     * @type {Boolean}
     */
    static override routable = true;

    // tslint:disable:variable-name
    protected _childrenIds?: number[];
    protected __childrenIds?: number[];
    protected _family?: Product[];
    protected __family?: Product[];
    // tslint:enable:variable-name

    @NgInject(TimeoutService)
    protected timeoutService!: TimeoutService;

    @NgInject(PriceService)
    protected priceService!: PriceService;

    @NgInject(StockResource)
    protected stockResource!: StockResource;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const loader = params.loader;
        // stock
        loader.add(this.resources.stock.get(this.id).load(1, params));

        // linked products
        this.getRelatedProductsToLoad(params).forEach(product =>
            loader.add(this.resources.product.get(product).load(this.verbosity!, { ...params, safe: true }))
        );
        // attributes
        loader.add(this.resources.attribute.loadForProduct(this.verbosity!));
    }

    protected getRelatedProductsToLoad(params: TLoadDependeciesParams): number[] {
        if (params.dependencyLevel >= EdependencyLevel.details) {
            const productIds = this.childrenIds;
            const data = <IProductDataV3>this.data;
            if (data && data.parent) {
                productIds.push(data.parent);
            }
            return productIds;
        }
        return [];
    }

    /**
     * Is simple getter
     *
     * usage:
     *
     * postModel.isSimple
     *
     */
    get isSimple(): boolean {
        return ((<IProductDataV2>this.data)?.type_id || 'simple') === 'simple';
    }

    // ////////////////////////////// BASIC ////////////////////////////////

    /**
     * Normalized brand getter
     *
     * Will search in `brand` and `manufacturer` attributes
     * if one is set for product then its label will be returned
     * as product's brand
     */
    get brand(): string | undefined {
        let brand = '';

        find(['brand', 'manufacturer'], attrName => {
            const value = this.data?.[attrName];
            if (value) {
                const attribute = <Attribute>this.app.resources.attribute.get(attrName);
                if (attribute) {
                    const val = attribute.getOptionLabel(value);
                    if (val) {
                        brand = Array.isArray(val) ? val.join(', ') : typeof val === 'number' ? val.toString() : val;
                        return true;
                    }
                }
            }
            return false;
        });

        return brand;
    }

    // ////////////////////////////// VARIANTS ////////////////////////////////
    /**
     * Is varaitns type of product check
     */
    get isVariants(): boolean {
        return this.data ? (<IProductDataV2>this.data).type_id === 'variants' : false;
    }

    // ////////////////////////////// CONFIGURABLE ////////////////////////////////

    /**
     * Is configurable getter
     *
     * usage:
     *
     * postModel.isConfigurable
     *
     */
    get isConfigurable(): boolean {
        return this.data ? (<IProductDataV2>this.data).type_id === 'configurable' : false;
    }

    /**
     * Children getter
     *
     * For configurable product returns all configurable children.
     * For bundle product returns all bundle children.
     *
     * usage:
     *
     * postModel.isConfigurable
     *
     */
    get childrenIds(): number[] {
        const data = <IProductDataV3>this.data;
        const options = data?.options;
        if (!this._childrenIds && options) {
            if (this.isConfigurable) {
                if (options?.configurable) {
                    this._childrenIds = uniq(flattenDeep(map(values(options.configurable), values)));
                }
            } else if (this.isBundle) {
                if (options?.bundle) {
                    this._childrenIds = map(flattenDeep(map(options.bundle, 'children')), 'id');
                }
            } else if (this.isGrouped) {
                if (options?.grouped) {
                    this._childrenIds = map(options.grouped, groupedOption => groupedOption.id);
                }
            }
        }
        const _childrenIds = this._childrenIds;
        if (!this.__childrenIds || !isEqual(_childrenIds, this.__childrenIds)) {
            this.__childrenIds = _childrenIds ? [..._childrenIds] : [];
        }

        return compact(this.__childrenIds);
    }

    get directChildrenIds(): number[] {
        return uniq((this.data as IProductDataV2).directChildrenIds || this.childrenIds);
    }

    /**
     * Children getter
     *
     * For configurable product returns all configurable children.
     * For bundle product returns all bundle children.
     *
     * usage:
     *
     * postModel.isConfigurable
     *
     */
    get children(): Product[] {
        return <Product[]>this.app.resources.product.getAll(this.childrenIds);
    }

    get directChildren(): Product[] {
        return <Product[]>this.app.resources.product.getAll(this.directChildrenIds);
    }

    /**
     * Same as children but only loaded ones will be returned
     * @return {Product[]} [description]
     */
    get existingChildren(): Product[] {
        return <Product[]>this.app.resources.product.getAllExisting(this.childrenIds);
    }

    /**
     * Default child getter
     *
     * For configurable product returns its default child
     * Currenty logic is to take first child but we should change it
     *
     * usage:
     *
     * postModel.defaultChild
     *
     * @todo Write proper logic
     *
     *
     */
    get defaultChild(): Product | undefined {
        return this.getDefaultChild();
    }
    protected getDefaultChild(): Product | undefined {
        const directChildrenIds = (<IProductDataV2>this.data).directChildrenIds;
        return this.existingChildren.find(item => !directChildrenIds || directChildrenIds.includes(item.id));
    }

    /**
     * Parent getter
     *
     * For configurable product returns all configurable children.
     * For bundle product returns all bundle children.
     *
     * usage:
     *
     * postModel.isConfigurable
     *
     */
    get parent(): Product {
        const data = <IProductDataV2>this.data;
        return (data && data.parent && <Product>this.app.resources.product.getCreate(data.parent)) || this;
    }

    /**
     * Direct parent getter
     *
     * Return direct parent of the product. If not exists returns undefined
     *
     * usage:
     *
     * postModel.directParent
     *
     */
    get directParent(): Product | undefined {
        const data = this.data as IProductDataV2;
        return data?.parent ? <Product>this.resource.getExisting(data?.parent) : undefined;
    }

    /**
     * Family
     *
     * For configurable product returns all configurable children.
     * For bundle product returns all bundle children.
     *
     * usage:
     *
     * postModel.isConfigurable
     *
     */
    get family(): Product[] {
        if (!this.__family) {
            const parent = this.parent;
            this.__family = union([parent], parent.children);
        }

        if (!isEqual(this._family, this.__family)) {
            this._family = this.__family;
        }

        return this._family!;
    }

    /**
     * configurableAttributeCodesgetter
     *
     * usage:
     *
     * postModel.configurableAttributeCodes
     *
     */
    get configurableAttributeCodes(): string[] | undefined {
        try {
            const data = <IProductDataV3>this.data;
            if (data) {
                if (data.parent) {
                    return this.parent.configurableAttributeCodes;
                }
                if (data.options && data.options.configurable) {
                    const attrCodes = keys(data.options.configurable);
                    const priority = intersection(this.getConfigurableAttributeCodesOrder(), attrCodes);
                    return [...priority, ...difference(attrCodes, priority)];
                }
            }
        } catch (err) {}
        return undefined;
    }

    /**
     * Ordered list of attributes
     */
    protected getConfigurableAttributeCodesOrder(): string[] {
        return [];
    }

    /**
     * Returns possible values of configurable attribute
     *
     * (we may have 100 colours in system, but family only uses 3 - this
     * will return those three)
     * @param {string} attributeCode [description]
     */
    getConfigurableAttributeOptions(attributeCode: string): Catalog.IAttributeOption[] {
        const data = <IProductDataV3>this.data;
        if (data?.options?.configurable) {
            // take all possible values
            const configurableValues = keys(data.options.configurable[attributeCode]).map(value => parseInt(value, 10));

            if (configurableValues?.length) {
                // take attribute
                const attribute = this.app.resources.attribute.get(attributeCode);

                // filter ops
                if (attribute && attribute.data && attribute.data.options) {
                    return filter(attribute.data.options, option => configurableValues.indexOf(option.value) !== -1);
                }
            }
        }
        return [];
    }

    /**
     * Checks if given option is in stock (salable)
     * @param  {string}    attributeCode Attribute to checj
     * @param  {number | IAttributeOption} option Option or its value
     * @return {boolean}  True if its in stock
     */
    isConfigurableOptionSalable(attributeCode: string, option: number | Catalog.IAttributeOption): boolean {
        const data = <IProductDataV3>this.data;
        if (data && data.options && data.options.configurable && data.options.configurable[attributeCode]) {
            const optionId = typeof option === 'number' ? option : option.value;
            const productIds = data.options.configurable[attributeCode][optionId];
            return !!find(productIds, (id: number) => {
                const product = this.app.resources.product.getExisting(id);
                return product && product.isSalable();
            });
        }
        return false;
    }

    /**
     * Takes media from product and its paren
     *
     * usage:
     *
     * postModel.familyMedia
     *
     * @todo fix types
     *
     */
    get familyMedia(): IProductMediaItem[] | undefined {
        const data = <IProductDataV3>this.data;
        if (this.isSimple && data.parent) {
            const parentMedia = (this.parent?.data as IProductDataV3)?.media;
            if (parentMedia) {
                if (!this.cache.familyMedia) {
                    const media = clone(data.media || []);
                    const images = map(media, item => item.image);
                    each(parentMedia, item => {
                        if (!images.includes(item.image)) {
                            media.push(
                                defaults(
                                    {
                                        position: item.position + images.length + 1
                                    },
                                    item
                                )
                            );
                        }
                    });
                    this.cache.familyMedia = media;
                }
                return this.cache.familyMedia;
            }
        }
        return data.media;
    }

    /**
     * Checks if product is new
     *
     * usage:
     *
     * postModel.isNew
     *
     */
    get isNew(): boolean {
        const data = <IProductDataV2>this.data;
        if (!data) {
            return false;
        }
        // is not if no data set
        if (!data.news_from_date || !data.news_to_date) {
            return false;
        }

        return inDateRange(data.news_from_date, data.news_to_date);
    }

    /**
     * Matches child exectly
     * If any required attribute (except ones we skip) is not set then will return undefined
     *
     */
    matchChildren(filterModel: IProductConfigurableSelection, skipAttributes?: string[] | boolean): Product[] | undefined {
        filterModel = omitBy(filterModel, value => typeof value === 'undefined');
        if (skipAttributes !== true) {
            const codes = this.configurableAttributeCodes;
            if (codes) {
                const options: string[] = <string[]>difference(codes, <string[]>skipAttributes);

                if (find(options, code => !filterModel[code])) {
                    return undefined;
                }
            }
        }

        const check = matches(filterModel);
        return this.existingChildren.filter((model: Product) => check(model.data));
    }

    matchChild(filterModel: IProductConfigurableSelection, skipAttributes?: string[] | boolean): Product | undefined {
        const matchingChildren = this.matchChildren(filterModel, skipAttributes);
        return matchingChildren && matchingChildren[0];
    }

    /**
     * Creates object containing configurable selection
     * For simple product it will return object containing values matching that project
     * For configurable product it will return object for its default child
     */
    createSelectionObject(): IProductConfigurableSelection | undefined {
        if (!this.data) {
            return undefined;
        }
        if (!this.isConfigurable && !this.isBundle) {
            const configurableAttributeCodes = this.parent.configurableAttributeCodes;
            const selectionObject: IProductConfigurableSelection = {};
            if (configurableAttributeCodes) {
                each(configurableAttributeCodes, attributeCode => {
                    const val = this.data![attributeCode];
                    if (val) {
                        selectionObject[attributeCode] = val;
                    }
                });
                return selectionObject;
            }
        }

        if (this.isConfigurable && this.defaultChild) {
            return this.defaultChild.createSelectionObject();
        }

        return undefined;
    }

    ////////////////////////////// GROUPED OPTIONS //////////////////////////////

    // NOTE: see also configurable section

    /**
     * Is gruoped product getter
     */
    get isGrouped(): boolean {
        return this.data ? (<IProductDataV2>this.data).type_id === 'grouped' : false;
    }

    ////////////////////////////// BUNDLE OPTIONS //////////////////////////////

    // NOTE: see also configurable section

    /**
     * Is bundle product getter
     *
     */
    get isBundle(): boolean {
        return this.data ? (<IProductDataV2>this.data).type_id === 'bundle' : false;
    }

    /**
     * Getter for product bundle options
     */
    get bundleOptions(): IProductDataBundleOption[] | undefined {
        const data = <IProductDataV3>this.data;
        if (data?.options?.bundle) {
            if (!this.cache.bundleOptions) {
                this.cache.bundleOptions = cloneDeep(data.options.bundle);
            }
            each(this.cache.bundleOptions, option => {
                each(option.children, (child: IProductDataBundleOptionChild) => {
                    child.product = <Product>this.app.resources.product.getExisting(child.id);
                });
            });
            return this.cache.bundleOptions;
        }
        return undefined;
    }

    /**
     * Cleans up data model
     *
     * Normalizes any options options
     * Makes sure that requries options are selected
     *
     * @param  {object} productModel Bundle product
     * @param  {IBundleSelection} data Data we want to set
     */
    createBundleSelectionObject(data?: IBundleSelectionOptionsData | BundleSelection): BundleSelection {
        return new BundleSelection(this, data);
    }

    /**
     * Same like createBundleSelectionObject but allows caching
     * @param  {IBundleSelectionOptionsData | BundleSelection} data [description]
     * @return {BundleSelection}                  [description]
     */
    getBundleSelectionObject(data?: IBundleSelectionOptionsData | BundleSelection): BundleSelection {
        return this.app.cache.get(
            ['product.getBundleSelectionObject', this.id, this.verbosity, data instanceof BundleSelection ? data.selection : data],
            () => this.createBundleSelectionObject(data)
        )!;
    }

    /**
     * Shorthand letting is take selection directly from product model
     * You will use it mostly when you have static bundle (no qties changes) and you
     * want to print children items on products page
     *
     * @param {IBundleSelectionOptionsData | BundleSelection} data [description]
     * @return {IBundleSelectionProduct[] | undefined}  Undefined when its no bundle
     *
     */
    getBundleSelectedProducts(data?: IBundleSelectionOptionsData | BundleSelection): IBundleSelectionProduct[] | undefined {
        if (this.isBundle) {
            return this.app.cache.get(
                ['product.getBundleSelectedProducts', this.id, data instanceof BundleSelection ? data.selection : data],
                () => {
                    return values(this.getBundleSelectionObject(data).getSelectedProducts());
                }
            );
        }
        return undefined;
    }

    ////////////////////////////// CUSTOM OPTIONS //////////////////////////////

    /**
     * CustomOptions getter
     *
     * usage:
     *
     * postModel.customOptions
     *
     */
    get customOptions(): IProductDataCustomOption[] | undefined {
        const data = <IProductDataV3>this.data;
        if (data && data.options) {
            return data.options.custom;
        }
        return undefined;
    }

    /**
     * Gets custom option by its name (uses regex to match)
     * @param  {string}                   name
     * @return {IProductDataCustomOption}
     */
    getCustomOption(name: string | RegExp): IProductDataCustomOption | undefined {
        const options = this.customOptions;
        if (options) {
            const reg = typeof name === 'string' ? new RegExp(name, 'i') : name;
            return options.find(option => option.title && option.title.match(reg));
        }
        return undefined;
    }

    /**
     * Gets custom option id by its name (uses regex to match)
     * @param  {string}                   name
     * @return {IProductDataCustomOption}
     */
    getCustomOptionId(name: string | RegExp): number | undefined {
        return this.app.cache.get(['product.getCustomOptionId', this.id, this.verbosity, name], () => {
            const option = this.getCustomOption(name);

            if (option) {
                return option.id;
            }
            return undefined;
        });
    }

    /**
     * Gets custom option by its id
     * @param {number | string} optionId Normally we expect it to be number,
     * but string format of number is fine too as we take it as object key
     */
    getCustomOptionById(optionId: number | string): IProductDataCustomOption | undefined {
        if (!this.cache.customOpsIndex) {
            this.cache.customOpsIndex = keyBy(this.customOptions, 'id') || {};
        }
        return this.cache.customOpsIndex[optionId];
    }

    getCustomOptionSelection(
        option: number | IProductDataCustomOption | undefined,
        value: undefined | number | number[]
    ): undefined | IProductDataCustomOptionSelection | IProductDataCustomOptionSelection[] {
        if (!value) {
            return undefined;
        }
        if (typeof option === 'number') {
            option = this.getCustomOptionById(<number>option);
        }
        if (option && option.values) {
            if (!option['_indexedValues']) {
                option['_indexedValues'] = keyBy(option.values, 'value');
            }
            return typeof value === 'number'
                ? option['_indexedValues'][value]
                : value.map(v => option!['_indexedValues'][v]).filter(i => !!i);
        }
        return undefined;
    }

    /**
     * Gets custom option by its name (uses regex to match)
     * @param  {string}                   name
     * @return {IProductDataCustomOption}
     */
    getCustomOptionSelectionByName(option: IProductDataCustomOption, name: string | RegExp): IProductDataCustomOptionSelection | undefined {
        const optionValues = option.values;
        if (optionValues && optionValues.length) {
            const reg = typeof name === 'string' ? new RegExp('^' + name + '$', 'i') : name;
            return <IProductDataCustomOptionSelection>(
                find(optionValues, (value: IProductDataCustomOptionSelection) => value.title && value.title.match(reg))
            );
        }
        return undefined;
    }

    ////////////////////////////// STOCK ////////////////////////////////////////

    get stock(): Stock | undefined {
        return this.stockResource.get(this.id);
    }

    /**
     * Checks if product is out of stock
     * Out of stock = website has manage stock on && item does manage stock && item is set as out of stock
     * (we do not check qty here)
     *
     * This feature is usually used on listing
     *
     * usage:
     *
     * productModel.isOutOfStock
     *
     */
    get isOutOfStock(): boolean {
        return this.stock?.isOutOfStock || false;
    }

    get isTempOutOfStock(): boolean {
        return this.stock?.isTempOutOfStock || false;
    }

    /**
     * True of product is in stock including filters applied
     * @returns
     */
    deepStockCheck(
        filters: {
            [attrCode: string]: number[];
        },
        direct = false
    ): boolean {
        if (this.isOutOfStock) {
            return false;
        }

        if (isEmpty(filters) || !(this.data as IProductDataV2)?.stockMap || !this.isConfigurable) {
            return true;
        }

        return this.inStockChildren(filters, direct).length > 0;
    }

    private inStockChildren(
        filters: {
            [attrCode: string]: number[];
        },
        direct = false
    ): number[] {
        if (!this.isConfigurable) {
            return [];
        }
        const stockMap = (this.data as IProductDataV2)?.stockMap;

        const allChildrenInitial = (direct && (this.data as IProductDataV2).directChildrenIds) || this.childrenIds;
        let allChildren = allChildrenInitial;

        // walk filters till any children still match
        find(filters, (filterValues, attrCode) => {
            // if filter is set
            if (filterValues.length) {
                const mapEntry = stockMap && stockMap[attrCode];
                // if map entry is set = any of children (direct and indirect) has values
                if (!isEmpty(mapEntry)) {
                    const childrenWithAnyFilterValue = flatten(Object.values(omit(mapEntry, 'undefined')));
                    // in direct mode we need to check if any of DIRECT children has values set, if not then skip test
                    if (direct && !find(allChildrenInitial, childId => childrenWithAnyFilterValue.includes(childId))) {
                        return false;
                    }
                    // reduce allowed children to the map entry only
                    allChildren = intersection(allChildren, flatten(Object.values(pick(mapEntry, filterValues))));
                }
            }
            // if no more children match then break
            return !allChildren.length;
        });

        return allChildren;
    }

    /**
     * Checks if qty 1 is in stock
     * @return {boolean}
     */
    public get inStock(): boolean {
        return this.isConfigurable || this.isBundle ? !this.isOutOfStock : this.isSalable(1);
    }

    /**
     * Checks max qty for given buy request (bundle only)
     *
     * @param  {boolean}    withBackorders
     * @param  {any}       buyRequest
     * @return {number}
     */
    getStockQty(
        withBackorders = false,
        buyRequest?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>,
        withBasket: TStockBasketTest = true
    ): number {
        if (this.isBundle) {
            const selection = this.getBundleSelectionObject(buyRequest ? buyRequest.bundle_options : undefined);
            return reduce(
                selection.getSelectedProducts(),
                function (memo, selectionProduct) {
                    // if any previous was out of stock, then its all out of stock already
                    if (!memo) {
                        return memo;
                    }
                    if (!selectionProduct.product) {
                        return 0;
                    }
                    // return lower value: memo (so other items max qty) or this items max qty
                    return Math.min(
                        memo,
                        Math.floor(selectionProduct.product.getStockQty(withBackorders, undefined, withBasket) / selectionProduct.qty)
                    );
                },
                10000
            );
        } else {
            const stock = this.stock;
            if (stock) {
                return stock.getQty(withBackorders, withBasket);
            }
            return 0;
        }
    }

    /**
     * Checks stock status of basket item
     *
     * returns:
     * 0 - out of stock
     * 1 - in stock
     * -1 - backorders only
     *
     */
    checkStockStatus(qty, buyRequest?: any): number {
        if (this.getStockQty(false, buyRequest) < qty) {
            return 0;
        }
        if (this.getStockQty(true, buyRequest) < qty) {
            return -1;
        }
        return 1;
    }

    /**
     * Checks if product is salable for given qty (in stock + backorders)
     * @param  {number}           qty
     * @param  {any}       buyRequest
     * @return {boolean}
     */
    isSalable(qty = 1, buyRequest?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>, withBasket: TStockBasketTest = true): boolean {
        return qty <= this.getStockQty(true, buyRequest, withBasket);
    }

    /**
     * Checks if product is in stock (skipping backorders) for given qty
     * @param  {number}           qty
     * @param  {any}       buyRequest
     * @return {boolean}
     */
    isInStock(qty = 1, buyRequest?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>, withBasket: TStockBasketTest = true): boolean {
        return qty <= this.getStockQty(false, buyRequest, withBasket);
    }

    isBackorderOnly(qty = 1, buyRequest?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>, withBasket = true): boolean {
        return this.isSalable(qty, buyRequest, withBasket) && !this.isInStock(qty, buyRequest, withBasket);
    }

    ////////////////////////////// PRICE //////////////////////////////

    /**
     * Gets current selection price
     * following selected custom options and bundle options
     *
     * @todo
     */
    protected calcPrice(
        orgPrice: boolean,
        request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'> | undefined,
        taxMode: TaxMode = TaxMode.auto,
        skipGroups = false,
        customerGroup = this.app.customer.model.data.group_id
    ): number {
        const data = this.data;

        if (!data) {
            return 0;
        }

        let price: number;

        if (this.isBundle && request && !((<IProductDataV2>this.data)?.price_type === EBundlePriceType.static)) {
            price = this.getBundleOptionsPrice(orgPrice, request, TaxMode.skip);
            if (!orgPrice && data.bundle_sp_perc) {
                price *= data.bundle_sp_perc / 100;
            }
        } else {
            price = this.calcBasePrice(orgPrice, request, skipGroups, customerGroup);
        }
        if (request) {
            price += this.getCustomOptionsPrice(request, TaxMode.skip);
        }
        return this.priceService.applyTax(price, data.tax, taxMode);
    }

    protected calcBasePrice(
        orgPrice: boolean,
        request: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'> | undefined,
        skipGroups: boolean,
        customerGroup?: number
    ) {
        const group: number | undefined = skipGroups ? undefined : customerGroup;
        const data = this.data;
        if (!data) {
            return 0;
        }
        const groupPrice: number | undefined | false =
            typeof group === 'number'
                ? orgPrice
                    ? data.group_org_price && data.group_org_price[group]
                    : data.group_price && data.group_price[group]
                : undefined;
        if (typeof groupPrice === 'number') {
            return groupPrice;
        }
        return <number>((orgPrice && data.org_price) || data.price);
    }

    /**
     * Returns product price depending on customer group
     * @return {number}
     */
    get price(): number {
        return this.getPrice();
    }

    get priceIncTax(): number {
        return this.getPrice(undefined, TaxMode.including);
    }

    getPrice(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'> | undefined, taxMode: TaxMode = TaxMode.auto): number {
        return this.calcPrice(false, request, taxMode) || 0;
    }

    /**
     * Returns product org price depending on customer group
     * @return {number}
     */
    get orgPrice(): number | undefined {
        return this.getOrgPrice();
    }

    getOrgPrice(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'> | undefined, taxMode: TaxMode = TaxMode.auto): number | undefined {
        const orgPrice = this.calcPrice(true, request, taxMode);
        if (orgPrice > this.getPrice(request, taxMode)) {
            return orgPrice;
        }
        return undefined;
    }

    /**
     * Collects custom options price by its selection
     */
    getCustomOptionsPrice(request: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>, taxMode: TaxMode = TaxMode.auto): number {
        let price = 0;
        if (request.options) {
            each(request.options, (optionValue: string | number | number[], optionId: string) => {
                if (optionValue) {
                    const option = this.getCustomOptionById(optionId);

                    if (option) {
                        switch (option.price_type) {
                            case 'length':
                                if (option.price) {
                                    price += ('' + optionValue).replace(/\s/g, '').length * option.price;
                                }
                                break;
                            default:
                                switch (option.type) {
                                    case 'field':
                                    case 'area':
                                        price += option.price || 0;
                                        break;
                                    case 'drop_down':
                                    case 'radio':
                                        price +=
                                            (
                                                this.getCustomOptionSelection(
                                                    option,
                                                    Number(optionValue)
                                                ) as IProductDataCustomOptionSelection
                                            )?.price || 0;
                                        break;
                                    case 'checkbox':
                                    case 'multiple':
                                        const customOptionSelection = this.getCustomOptionSelection(option, optionValue as number[]) as
                                            | IProductDataCustomOptionSelection[]
                                            | undefined;

                                        if (Array.isArray(customOptionSelection)) {
                                            price += customOptionSelection.reduce((v, i) => v + (i.price || 0), 0);
                                        }
                                        break;
                                }
                        }
                    }
                }
            });
        }
        return this.data ? this.priceService.applyTax(price, this.data.tax, taxMode) : 0;
    }

    /**
     * Retrieves selection price by request
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    getBundleOptionsPrice(
        orgPrices: boolean,
        request: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>,
        taxMode: TaxMode = TaxMode.auto
    ): number {
        if (this.isBundle && this.data) {
            const selection = this.getBundleSelectionObject(request?.bundle_options);
            const price = orgPrices ? selection.getOrgPrice() : selection.getPrice();
            return this.priceService.applyTax(price, this.data.tax, taxMode);
        }
        return 0;
    }

    protected getListingLabel(): number | undefined {
        if (this.hasV2) {
            return (<IProductDataV2>this.data).listing_label;
        }
        return undefined;
    }

    protected getLabel() {
        return optionLabelPipe(this.getListingLabel(), 'listing_label') as string | undefined;
    }

    get label(): string | undefined {
        return this.getLabel();
    }

    get labelShort(): string | undefined {
        const label = this.label;
        return typeof label === 'string' ? label.split(':')[0] : undefined;
    }

    protected getLabelColor() {
        const label = this.getListingLabel();
        return label ? optionColorPipe(label, 'listing_label') : undefined;
    }

    get labelColor(): string | undefined {
        return this.getLabelColor();
    }

    /**
     * Retrieves attribute from this or parent
     */
    getAttr<RT>(attrCode: string): RT {
        return (this.data && this.data[attrCode]) || (this.parent.data && this.parent.data[attrCode]);
    }
}
